<template>
  <div class="adm-slots">
    <div class="adm-card">
      <div class="adm-card__body">
        <div class="adm-grid">
          <div class="adm-field">
            <label class="adm-label" for="slot-doctor">Doctor</label>
            <select id="slot-doctor" class="adm-select" :value="doctorId || ''" @change="selectDoctor($event.target.value)">
              <option value="">{{ doctorsLoading ? "Loading doctors…" : "Select doctor" }}</option>
              <option v-for="doctor in doctors" :key="doctor.doctorid" :value="doctor.doctorid">
                {{ doctor.doctorname }}{{ doctor.status === "active" ? "" : ` (${doctor.status})` }}
              </option>
            </select>
          </div>
          <div class="adm-field">
            <label class="adm-label" for="slot-clinic">Clinic</label>
            <select id="slot-clinic" v-model="clinicId" class="adm-select" :disabled="!doctorId || !clinics.length">
              <option value="">{{ doctorId && !clinics.length ? "Doctor has no clinics" : "Select clinic" }}</option>
              <option v-for="clinic in clinics" :key="clinic.clinicid" :value="clinic.clinicid">{{ clinic.clinicname }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!doctorId || !clinicId" class="adm-card adm-empty adm-slots-gap">
      <i class="fas fa-calendar-alt"></i>
      <strong>Pick a doctor and clinic</strong>
      You'll see the doctor's weekly schedule and can generate bookable slots from it.
    </div>

    <template v-else>
      <div class="adm-card adm-slots-gap">
        <div class="adm-card__header">
          <div>
            <h3 class="adm-card__title">Weekly schedule</h3>
            <p class="adm-card__hint">Set by the doctor in their schedule settings.</p>
          </div>
        </div>
        <div class="adm-card__body">
          <div v-if="!clinicSchedules.length" class="adm-alert adm-alert--warning adm-slots-flush">
            <i class="fas fa-exclamation-triangle"></i>
            <div>No schedule for this clinic yet. Ask the doctor to add their timings before generating slots.</div>
          </div>
          <div v-else class="adm-schedule">
            <div v-for="schedule in clinicSchedules" :key="schedule.slno" class="adm-schedule__row">
              <strong>{{ schedule.weekday }}</strong>
              <span>{{ time(schedule.starttime) }} – {{ time(schedule.endtime) }}</span>
              <span class="adm-chip">{{ schedule.appointduration }} min</span>
            </div>
          </div>
        </div>
      </div>

      <div class="adm-card adm-slots-gap">
        <div class="adm-card__header">
          <div>
            <h3 class="adm-card__title">Generate slots</h3>
            <p class="adm-card__hint">Creates bookable slots for each scheduled day in the range.</p>
          </div>
        </div>
        <div class="adm-card__body">
          <div class="adm-slots-generate">
            <div class="adm-field">
              <label class="adm-label" for="slot-start">From</label>
              <input id="slot-start" v-model="startDate" type="date" class="adm-input" @change="fetchSlots" />
            </div>
            <div class="adm-field">
              <label class="adm-label" for="slot-end">To</label>
              <input id="slot-end" v-model="endDate" type="date" class="adm-input" :min="startDate" @change="fetchSlots" />
            </div>
            <button type="button" class="adm-btn adm-btn--primary" :disabled="generating || !clinicSchedules.length" @click="generate">
              <span v-if="generating" class="adm-spinner"></span>
              <i v-else class="fas fa-calendar-check"></i> Generate slots
            </button>
          </div>
        </div>
      </div>

      <div class="adm-card adm-slots-gap">
        <div class="adm-card__header">
          <h3 class="adm-card__title">Existing slots</h3>
          <div class="adm-toolbar__spacer"></div>
          <button type="button" class="adm-icon-btn" title="Refresh" aria-label="Refresh slots" :disabled="slotsLoading" @click="fetchSlots">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': slotsLoading }"></i>
          </button>
        </div>
        <div class="adm-card__body">
          <p v-if="!clinicSlots.length" class="adm-muted adm-slots-flush">No slots in this date range.</p>
          <div v-else class="adm-slot-days">
            <div v-for="slot in clinicSlots" :key="`${slot.clinicid}-${slot.day}`" class="adm-slot-day">
              <div class="adm-slot-day__date">{{ formatDate(slot.day) }}</div>
              <div>
                <span v-for="t in slot.times.filter(Boolean)" :key="t" class="adm-chip">{{ t }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { errorMessage, formatDate } from "../admin-utils";

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

export default {
  name: "SlotsPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  props: {
    intent: { type: Object, default: null },
  },
  emits: ["notify"],
  data() {
    const end = new Date();
    end.setDate(end.getDate() + 30);
    return {
      doctors: [],
      doctorsLoading: false,
      doctorId: null,
      clinics: [],
      clinicId: "",
      schedules: [],
      slots: [],
      slotsLoading: false,
      generating: false,
      startDate: isoDate(new Date()),
      endDate: isoDate(end),
    };
  },
  computed: {
    clinicSchedules() {
      return this.schedules.filter(s => s.clinicid === this.clinicId);
    },
    clinicSlots() {
      return this.slots.filter(s => s.clinicid === this.clinicId);
    },
  },
  methods: {
    formatDate,
    time(value) {
      return value ? String(value).slice(0, 5) : "";
    },
    async fetchDoctors() {
      this.doctorsLoading = true;
      try {
        const { data } = await axios.get("/doctors", { params: { status: "all", per_page: 100 } });
        this.doctors = (data.doctors || [])
          .filter(d => d.status === "active" || d.status === "inactive")
          .sort((a, b) => a.doctorname.localeCompare(b.doctorname));
      } catch (error) {
        if (!error.response || error.response.status !== 404) {
          this.$emit("notify", errorMessage(error, "Could not load doctors."), "error");
        }
      } finally {
        this.doctorsLoading = false;
      }
    },
    async selectDoctor(value) {
      this.doctorId = value ? Number(value) : null;
      this.clinics = [];
      this.clinicId = "";
      this.schedules = [];
      this.slots = [];
      if (!this.doctorId) return;
      const doctorId = this.doctorId;
      try {
        const [clinics, schedules] = await Promise.all([
          axios.get(`/doctors/${doctorId}/clinicids`, { params: { details: true } }),
          axios.get(`/doctors/${doctorId}/scheduletiming`),
        ]);
        if (doctorId !== this.doctorId) return;
        this.clinics = clinics.data || [];
        this.schedules = schedules.data || [];
        if (this.clinics.length) this.clinicId = this.clinics[0].clinicid;
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not load the doctor's clinics."), "error");
      }
      await this.fetchSlots();
    },
    async fetchSlots() {
      if (!this.doctorId || !this.startDate || !this.endDate) return;
      this.slotsLoading = true;
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/slots`, {
          params: { starttime: this.startDate, endtime: this.endDate },
        });
        this.slots = (data && data.timeslots) || [];
      } catch (error) {
        this.slots = [];
        if (!error.response || error.response.status !== 404) {
          this.$emit("notify", errorMessage(error, "Could not load slots."), "error");
        }
      } finally {
        this.slotsLoading = false;
      }
    },
    async generate() {
      if (!this.startDate || !this.endDate || this.startDate > this.endDate) {
        this.$emit("notify", "Choose a valid date range.", "error");
        return;
      }
      this.generating = true;
      try {
        const { data } = await axios.post(`/doctors/${this.doctorId}/generate-slot/${encodeURIComponent(this.clinicId)}`, {
          startdate: this.startDate,
          enddate: this.endDate,
        });
        this.$emit("notify", (data && data.detail) || "Slots generated.");
        await this.fetchSlots();
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not generate slots."), "error");
      } finally {
        this.generating = false;
      }
    },
  },
  created() {
    this.fetchDoctors();
    if (this.intent && this.intent.doctorid) this.selectDoctor(this.intent.doctorid);
  },
};
</script>

<style scoped>
.adm-slots-gap {
  margin-top: 16px;
}

.adm-slots .adm-card__body .adm-grid .adm-field,
.adm-slots-generate .adm-field {
  margin-bottom: 0;
}

.adm-slots-flush {
  margin: 0;
}

.adm-schedule {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
}

.adm-schedule__row {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--adm-line);
  border-radius: var(--adm-radius-sm);
  padding: 10px 12px;
}

.adm-schedule__row strong {
  color: var(--adm-ink);
  min-width: 84px;
}

.adm-schedule__row .adm-chip {
  margin-left: auto;
}

.adm-slots-generate {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
}

.adm-slot-days {
  display: grid;
  gap: 10px;
  max-height: 420px;
  overflow-y: auto;
}

.adm-slot-day {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 12px;
  border-bottom: 1px solid var(--adm-line-2);
  padding-bottom: 10px;
}

.adm-slot-day__date {
  font-weight: 600;
  color: var(--adm-ink);
}

@media (max-width: 640px) {
  .adm-slot-day {
    grid-template-columns: 1fr;
  }
}
</style>
